import React, { useEffect, useState } from 'react'
import { FlatList, RefreshControl, useWindowDimensions, Platform } from 'react-native'
import { Heart, Store } from 'lucide-react-native'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { supabase } from '../lib/supabase'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { ProductCard, EmptyState, Button, ButtonText, Text, Spinner, YStack } from '@fliisi/pollen'

const GRID_PADDING = 20
const CARD_GAP = 12

interface FavoriteProduct {
  id: string
  product: {
    id: string; title: string; title_en: string | null; title_fi: string | null
    price: number; status: string; product_images: { image_url: string }[]
  }
}
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Favorites'>
}

export default function FavoritesScreen({ navigation }: Props) {
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const { width: windowWidth } = useWindowDimensions()
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const contentWidth = Math.min(windowWidth, 768)
  const numColumns = contentWidth > 500 ? 3 : 2
  const cardWidth = (contentWidth - GRID_PADDING * 2 - CARD_GAP * (numColumns - 1)) / numColumns

  const getTitle = (product: FavoriteProduct['product']) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const fetchData = async () => {
    if (!user?.id) return
    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('id, product:products(id, title, title_en, title_fi, price, status, flea_market_id, product_images(image_url), store:flea_markets(id, name))')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      const valid = (data || [])
        .filter((f: any) => f.product !== null)
        .map((f: any) => ({ id: f.id, product: Array.isArray(f.product) ? f.product[0] : f.product }))
        .filter((f: any) => f.product) as FavoriteProduct[]
      setFavorites(valid)
    } catch (e) { console.error('Error fetching favorites:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }

  useEffect(() => {
    if (!user?.id) { navigation.replace('Login'); return }
    fetchData()
  }, [user?.id])

  const handleRemove = async (favoriteId: string) => {
    try {
      await supabase.from('favorites').delete().eq('id', favoriteId)
      setFavorites(prev => prev.filter(f => f.id !== favoriteId))
    } catch (e) { console.error('Error removing favorite:', e) }
  }

  const renderItem = ({ item }: { item: FavoriteProduct }) => {
    const p = item.product
    return (
      <ProductCard
        w={cardWidth}
        imageUri={p.product_images?.[0]?.image_url}
        title={getTitle(p)}
        price={`${p.price?.toFixed(2)} €`}
        shopIcon={<Store size={12} color="#858B80" />}
        shopName={(p as any).store?.name || t('store') || 'Kauppa'}
        isFavorited={true}
        onPress={() => navigation.navigate('ProductView', { productId: p.id })}
        onHeartPress={() => handleRemove(item.id)}
      />
    )
  }

  return (
    <YStack f={1} bg="$background">
      <YStack px="$lg" pt={Platform.OS === 'web' ? 20 : 56} pb="$sm">
        <Text fontFamily="$cursive" fontSize={24} color="$color">{t('favorites')}</Text>
        {!loading && <Text preset="bodySmall" tone="secondary" mt={4}>{favorites.length} {t('saved_items')}</Text>}
      </YStack>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <FlatList
          key={`fav-grid-${numColumns}`}
          data={favorites}
          keyExtractor={item => item.id}
          numColumns={numColumns}
          columnWrapperStyle={{ gap: CARD_GAP, marginBottom: CARD_GAP }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData() }} />}
          contentContainerStyle={{ paddingHorizontal: GRID_PADDING, paddingBottom: 100, paddingTop: 4, maxWidth: 768, alignSelf: 'center', width: '100%' }}
          ListEmptyComponent={
            <EmptyState
              icon={<Heart size={48} color="#748C70" />}
              title={t('no_favorites')}
              description={t('favorites_hint')}
              action={
                <Button variant="primary" onPress={() => navigation.navigate('BuyerHome')}>
                  <ButtonText>{t('browse_products')}</ButtonText>
                </Button>
              }
            />
          }
          renderItem={renderItem}
        />
      )}
    </YStack>
  )
}
